import { useRef, useState, useEffect, useMemo, useCallback } from 'react'
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardBody,
  Heading,
  HStack,
  VStack,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Image,
} from '@chakra-ui/react'
import { Search, Grid3x3, List, CheckSquare, Square } from 'lucide-react'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { MediaGrid } from '@/components/media/MediaGrid'
import { MediaService } from '@/lib/services/mediaService'
import { MediaUpload } from '@/components/media/MediaUpload'
import { MediaEditModal } from '@/components/media/MediaEditModal'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useAccessCheck } from '@/hooks/useAccess'
import { useThemeColors } from '@/hooks/useThemeColors'
import type { Media, MediaListParams, MediaDateFolder } from '@/types/media'

type ViewMode = 'grid' | 'list'

export default function MediaLibraryPage() {
  const colors = useThemeColors()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const selectedIdsRef = useRef<number[]>([])
  const { showError, showSuccess, showWarning } = useToastNotification()
  const { isOpen: isUploadOpen, onOpen: onUploadOpen, onClose: onUploadClose } = useDisclosure()
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure()
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure()
  const { isOpen: isDetailsOpen, onOpen: onDetailsOpen, onClose: onDetailsClose } = useDisclosure()

  const [viewMode, setViewMode] = useState<ViewMode>('grid')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedType, setSelectedType] = useState<string>('')
  const [selectedDate, setSelectedDate] = useState<string>('')
  const [dateFolders, setDateFolders] = useState<MediaDateFolder[]>([])
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [isBulkDeleting, setIsBulkDeleting] = useState(false)
  const [editingMedia, setEditingMedia] = useState<Media | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Media | null>(null)
  const [deleteMessage, setDeleteMessage] = useState('')
  const [isBulkSelectMode, setIsBulkSelectMode] = useState(false)
  const [selectedMediaDetails, setSelectedMediaDetails] = useState<Media | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  const access = useAccessCheck()

  useEffect(() => {
    const fetchDateFolders = async () => {
      try {
        const folders = await MediaService.getDateFolders()
        setDateFolders(folders)
      } catch (error: any) {
        console.error('Failed to fetch date folders:', error)
      }
    }
    fetchDateFolders()
  }, [])

  useEffect(() => {
    selectedIdsRef.current = selectedIds
  }, [selectedIds])

  useEffect(() => {
    if (viewMode === 'list' && tableRef.current) {
      const interval = setInterval(() => {
        const selected = tableRef.current?.getSelectedRows() || []
        const currentStr = JSON.stringify([...selected].sort((a, b) => a - b))
        const prevStr = JSON.stringify([...selectedIdsRef.current].sort((a, b) => a - b))
        if (currentStr !== prevStr) {
          setSelectedIds(selected)
        }
      }, 300)

      return () => clearInterval(interval)
    } else if (viewMode === 'grid') {
      setSelectedIds([])
    }
  }, [viewMode])

  const typeFilterOptions = useMemo(
    () => [
      { value: 'image', label: 'Images' },
      { value: 'video', label: 'Videos' },
      { value: 'audio', label: 'Audio' },
      { value: 'application', label: 'Documents' },
    ],
    []
  )

  const mediaParams = useMemo<MediaListParams>(
    () => ({
      mode: viewMode,
      search: searchQuery || undefined,
      type: selectedType || undefined,
      date: selectedDate || undefined,
    }),
    [viewMode, searchQuery, selectedType, selectedDate]
  )

  const tableParams = useMemo(
    () => ({
      search: searchQuery,
      type: selectedType || undefined,
      date: selectedDate || undefined,
    }),
    [searchQuery, selectedType, selectedDate]
  )

  const handleSingleDeleteClick = useCallback(
    (media: Media) => {
      setDeleteMessage('Are you sure you want to delete this media item? This action cannot be undone.')
      setDeleteTarget(media)
      onDeleteModalOpen()
    },
    [onDeleteModalOpen]
  )

  const handleBulkDeleteClick = () => {
    if (selectedIds.length === 0) {
      showWarning('Please select at least one media item')
      return
    }
    setDeleteMessage(`Are you sure you want to delete ${selectedIds.length} media item(s)? This action cannot be undone.`)
    setDeleteTarget(null)
    onDeleteModalOpen()
  }

  const handleSearch = () => {
    const query = searchRef.current?.value || ''
    setSearchQuery(query)
    if (viewMode === 'list') {
      tableRef.current?.applyFilter('search', query)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const tableOptions = useMemo<DataTableOptions>(
    () => ({
      dataSource: '/content-management/media-library',
      dataFields: {
        file_name: {
          name: 'File Name',
          withSort: true,
          attachment: 'thumbnail_url',
          downloadUrl: 'file_url',
        },
        file_type: { name: 'File Type', withSort: true },
        file_dimensions: { name: 'Dimensions' },
        formatted_file_size: { name: 'Size' },
        created_at: { name: 'Uploaded', withSort: true },
      },
      softDelete: false,
      primaryKey: 'id',
      displayInModal: false,
      otherActions: [
        {
          name: 'Edit',
          onClick: (row: Media) => {
            setEditingMedia(row)
            onEditOpen()
          },
        },
        {
          name: 'Delete',
          onClick: handleSingleDeleteClick,
        },
      ],
    }),
    [handleSingleDeleteClick, onEditOpen]
  )

  const handleConfirmDelete = async () => {
    setIsBulkDeleting(true)
    try {
      if (deleteTarget) {
        await MediaService.deleteMedia(deleteTarget.id)
        showSuccess('Media deleted successfully')
      } else {
        await MediaService.bulkDeleteMedia({ ids: selectedIds })
        showSuccess(`${selectedIds.length} media item(s) deleted successfully`)
        setSelectedIds([])
      }

      if (viewMode === 'list') {
        tableRef.current?.reload()
        setTimeout(() => {
          const tbodyRef = (tableRef.current as any)?.tbodyRef
          if (tbodyRef?.current) {
            tbodyRef.current.checkedAll(false)
          }
        }, 100)
      } else {
        setRefreshKey((prev) => prev + 1)
      }

      onDeleteModalClose()
      setDeleteTarget(null)
    } catch (error: any) {
      showError(error)
    } finally {
      setIsBulkDeleting(false)
    }
  }

  const handleUploadSuccess = () => {
    onUploadClose()
    if (viewMode === 'list') {
      tableRef.current?.reload()
    } else {
      setRefreshKey((prev) => prev + 1)
    }
  }

  const handleEditSuccess = () => {
    onEditClose()
    setEditingMedia(null)
    if (viewMode === 'list') {
      tableRef.current?.reload()
    } else {
      setRefreshKey((prev) => prev + 1)
    }
  }

  const handleBulkSelectToggle = () => {
    setIsBulkSelectMode(!isBulkSelectMode)
    if (isBulkSelectMode) {
      setSelectedIds([])
    }
  }

  const handleMediaItemClick = (media: Media) => {
    if (!isBulkSelectMode) {
      setSelectedMediaDetails(media)
      onDetailsOpen()
    }
  }

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
            <CardHeader py={0} px={0} flexShrink={0}>
              <Box pt={2} pb={0}>
                <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                  <Box pb={2}>
                    <Heading size="md" mb={1}>
                      Media Library
                    </Heading>
                    <Text fontSize="sm" color={colors.text}>
                      Manage your media files
                    </Text>
                  </Box>
                  <Box pb={2}>
                    {access.can_create && (
                      <Button size="sm" colorScheme="blue" onClick={onUploadOpen}>
                        Upload Media
                      </Button>
                    )}
                  </Box>
                </HStack>
                <Box borderBottom="2px" borderColor={colors.border} mx={4} />
              </Box>
            </CardHeader>

            <CardHeader py={1} pt={3} pb={2} flexShrink={0}>
              <HStack spacing={4} flexWrap="nowrap" align="center">
                <HStack spacing={1} border="1px" borderColor={colors.border} borderRadius="md" p={1} flexShrink={0}>
                  <IconButton
                    aria-label='Grid view'
                    icon={<Grid3x3 size={18} />}
                    size="sm"
                    variant={viewMode === 'grid' ? 'solid' : 'ghost'}
                    colorScheme={viewMode === 'grid' ? 'blue' : 'gray'}
                    onClick={() => setViewMode('grid')}
                  />
                  <IconButton
                    aria-label='List view'
                    icon={<List size={18} />}
                    size="sm"
                    variant={viewMode === 'list' ? 'solid' : 'ghost'}
                    colorScheme={viewMode === 'list' ? 'blue' : 'gray'}
                    onClick={() => setViewMode('list')}
                  />
                </HStack>

                {viewMode === 'grid' && (
                  <Button
                    size="sm"
                    variant={isBulkSelectMode ? 'solid' : 'outline'}
                    colorScheme={isBulkSelectMode ? 'blue' : 'gray'}
                    leftIcon={isBulkSelectMode ? <CheckSquare size={16} /> : <Square size={16} />}
                    onClick={handleBulkSelectToggle}
                    flexShrink={0}
                  >
                    {isBulkSelectMode ? 'Cancel Selection' : 'Select Multiple'}
                  </Button>
                )}

                {selectedIds.length > 0 && access.can_delete && (
                  <HStack spacing={2} flexShrink={0}>
                    <Text fontSize="sm" color={colors.text} whiteSpace="nowrap">
                      {selectedIds.length} selected
                    </Text>
                    <Button
                      size="sm"
                      colorScheme="red"
                      onClick={handleBulkDeleteClick}
                      isLoading={isBulkDeleting}
                      flexShrink={0}
                    >
                      Delete Selected
                    </Button>
                  </HStack>
                )}

                <Select
                  placeholder="All Types"
                  size="sm"
                  value={selectedType}
                  onChange={(e) => {
                    e.stopPropagation()
                    setSelectedType(e.target.value)
                  }}
                  flexShrink={0}
                  w="150px"
                >
                  {typeFilterOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>

                <Select
                  placeholder="All Dates"
                  size="sm"
                  value={selectedDate}
                  onChange={(e) => {
                    e.stopPropagation()
                    setSelectedDate(e.target.value)
                  }}
                  flexShrink={0}
                  w="200px"
                >
                  {dateFolders.map((folder) => (
                    <option key={folder.value} value={folder.value}>
                      {folder.label}
                    </option>
                  ))}
                </Select>

                <Box flex="1" minW="250px" maxW="400px">
                  <InputGroup size="sm">
                    <Input
                      ref={searchRef}
                      placeholder="Search media..."
                      onKeyDown={handleKeyDown}
                      defaultValue={searchQuery}
                      pr="2.5rem"
                    />
                    <InputRightElement width="2.5rem">
                      <IconButton
                        aria-label="Search"
                        icon={<Search size={16} />}
                        size="sm"
                        h="1.75rem"
                        w="1.75rem"
                        onClick={handleSearch}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </HStack>
            </CardHeader>

            <CardBody
              p={0}
              pt={1}
              pb={4}
              mx={5}
              flex="1"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              minH={0}
              position="relative"
            >
              {viewMode === 'grid' ? (
                <MediaGrid
                  key={refreshKey}
                  params={mediaParams}
                  selectable={isBulkSelectMode}
                  selectedIds={selectedIds}
                  onSelectionChange={setSelectedIds}
                  onSelect={handleMediaItemClick}
                  onEdit={(media) => {
                    setEditingMedia(media)
                    onEditOpen()
                  }}
                  onDelete={handleSingleDeleteClick}
                  onUploadComplete={() => setRefreshKey((prev) => prev + 1)}
                />
              ) : (
                <Box h="100%" display="flex" flexDirection="column" minH={0}>
                  <DataTable
                    ref={tableRef}
                    options={tableOptions}
                    params={tableParams}
                  />
                </Box>
              )}
            </CardBody>
          </Card>
        </VStack>
      </Box>

      <MediaUpload isOpen={isUploadOpen} onClose={onUploadClose} onSuccess={handleUploadSuccess} />

      {editingMedia && (
        <MediaEditModal
          isOpen={isEditOpen}
          onClose={onEditClose}
          media={editingMedia}
          onSuccess={handleEditSuccess}
        />
      )}

      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirmation</ModalHeader>
          <ModalBody>
            <Text>{deleteMessage}</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteModalClose} isDisabled={isBulkDeleting}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={handleConfirmDelete} isLoading={isBulkDeleting} loadingText="Deleting...">
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isDetailsOpen} onClose={onDetailsClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Media Details</ModalHeader>
          <ModalBody>
            {selectedMediaDetails && (
              <VStack spacing={4} align="stretch">
                <Box>
                  {selectedMediaDetails.is_image && (
                    <Image
                      src={selectedMediaDetails.file_url}
                      alt={selectedMediaDetails.file_name}
                      maxH="400px"
                      objectFit="contain"
                      borderRadius="md"
                      w="100%"
                    />
                  )}
                  {selectedMediaDetails.is_video && (
                    <Box as="video" src={selectedMediaDetails.file_url} controls w="100%" borderRadius="md" />
                  )}
                  {selectedMediaDetails.is_audio && (
                    <Box as="audio" src={selectedMediaDetails.file_url} controls w="100%" />
                  )}
                  {!selectedMediaDetails.is_image &&
                    !selectedMediaDetails.is_video &&
                    !selectedMediaDetails.is_audio && (
                      <Box
                        p={8}
                        bg={colors.sectionBg}
                        border="1px"
                        borderColor={colors.border}
                        borderRadius="md"
                        textAlign="center"
                      >
                        <Text color={colors.textMuted}>Preview not available</Text>
                      </Box>
                    )}
                </Box>

                <VStack spacing={3} align="stretch">
                  <Box>
                    <Text fontSize="sm" color={colors.textMuted} mb={1}>
                      File Name
                    </Text>
                    <Text fontWeight="medium" color={colors.text}>
                      {selectedMediaDetails.file_name}
                    </Text>
                  </Box>

                  <HStack spacing={4} fontSize="sm">
                    <Box>
                      <Text color={colors.textMuted} mb={1}>
                        File Type
                      </Text>
                      <Text fontWeight="medium" color={colors.text}>
                        {selectedMediaDetails.file_type}
                      </Text>
                    </Box>
                    <Box>
                      <Text color={colors.textMuted} mb={1}>
                        File Size
                      </Text>
                      <Text fontWeight="medium" color={colors.text}>
                        {selectedMediaDetails.formatted_file_size ||
                          `${selectedMediaDetails.file_size} B`}
                      </Text>
                    </Box>
                    {selectedMediaDetails.file_dimensions && (
                      <Box>
                        <Text color={colors.textMuted} mb={1}>
                          Dimensions
                        </Text>
                        <Text fontWeight="medium" color={colors.text}>
                          {selectedMediaDetails.file_dimensions}
                        </Text>
                      </Box>
                    )}
                  </HStack>

                  <Box>
                    <Text fontSize="sm" color={colors.textMuted} mb={1}>
                      Uploaded
                    </Text>
                    <Text fontWeight="medium" color={colors.text}>
                      {selectedMediaDetails.created_at}
                    </Text>
                  </Box>

                  {selectedMediaDetails.caption && (
                    <Box>
                      <Text fontSize="sm" color={colors.textMuted} mb={1}>
                        Caption
                      </Text>
                      <Text color={colors.text}>{selectedMediaDetails.caption}</Text>
                    </Box>
                  )}

                  {selectedMediaDetails.short_descriptions && (
                    <Box>
                      <Text fontSize="sm" color={colors.textMuted} mb={1}>
                        Description
                      </Text>
                      <Text color={colors.text}>{selectedMediaDetails.short_descriptions}</Text>
                    </Box>
                  )}

                  <Box>
                    <Text fontSize="sm" color={colors.textMuted} mb={2}>
                      File URL
                    </Text>
                    <HStack>
                      <Input value={selectedMediaDetails.file_url} isReadOnly fontSize="sm" />
                      <Button
                        size="sm"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(selectedMediaDetails.file_url)
                            showSuccess('URL copied to clipboard')
                          } catch {
                            showError(new Error('Failed to copy URL'))
                          }
                        }}
                      >
                        Copy
                      </Button>
                    </HStack>
                  </Box>
                </VStack>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <HStack spacing={2}>
              {access.can_edit && (
                <Button
                  colorScheme="blue"
                  onClick={() => {
                    onDetailsClose()
                    setEditingMedia(selectedMediaDetails)
                    onEditOpen()
                  }}
                >
                  Edit
                </Button>
              )}
              <Button variant="ghost" onClick={onDetailsClose}>
                Close
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  )
}


